import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../components/graph/graphConfig';
import ResultSearchBar from '../components/projects/ResultSearchBar';
import ResultsFilterRow, { ResultFilters } from '../components/projects/ResultsFilterRow';
import ResultsTable from '../components/projects/ResultsTable';
import { getProjectDetails, getProjectsLanding, searchResults } from '../services/apiService';
import type {
  ProjectDetail,
  ProjectsLandingStats,
  ProjectResult,
  ProjectSearchResponse,
} from '../types/api';

const FEATURED_OUTCOME_IDS: number[] = [18808, 31760];

type Op = 'AND' | 'OR';

interface CompetencyExample {
  keywords: string[];
  op: Op;
}

const COMPETENCY_EXAMPLES: CompetencyExample[] = [
  { keywords: ['bim', 'deep learning'], op: 'AND' },
  { keywords: ['robotics', 'automation'], op: 'OR' },
];

const exampleToQuery = (e: CompetencyExample): string =>
  e.keywords.join(` ${e.op} `);

interface FeaturedOutcome {
  id: number;
  name: string;
  year: number;
  type: string;
  facultyAbbr: string | null;
}

function pickFacultyAbbr(detail: ProjectDetail): string | null {
  // The detail returns `faculty` as a full Czech name, but each author has
  // `faculty_abbr` already abbreviated. Use the first author's abbreviation.
  const firstAbbr = detail.people?.[0]?.faculty_abbr;
  if (firstAbbr && FACULTY_COLORS[firstAbbr]) return firstAbbr;
  return null;
}

function readFiltersFromParams(params: URLSearchParams): ResultFilters {
  const yf = params.get('year_from');
  const yt = params.get('year_to');
  const fac = params.get('faculties');
  const tp = params.get('types');
  return {
    yearFrom: yf ? Number(yf) : undefined,
    yearTo: yt ? Number(yt) : undefined,
    faculties: fac ? fac.split(',').map((s) => s.trim()).filter(Boolean) : [],
    types: tp ? tp.split(',').map((s) => s.trim()).filter(Boolean) : [],
  };
}

function readQueryFromParams(params: URLSearchParams): { keywords: string[]; ops: Op[] } | null {
  const q = params.get('q');
  if (!q) return null;
  const keywords = q.split(',').map((s) => s.trim()).filter(Boolean);
  if (keywords.length === 0) return null;
  const opsRaw = params.get('ops');
  const ops: Op[] = opsRaw
    ? opsRaw.split(',').map((o) => (o.trim().toUpperCase() === 'OR' ? 'OR' : 'AND'))
    : keywords.slice(1).map(() => 'AND' as Op);
  return { keywords, ops };
}

export default function ProjectsPage() {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [stats, setStats] = useState<ProjectsLandingStats | null>(null);
  const [featured, setFeatured] = useState<FeaturedOutcome[]>([]);
  const [landingLoading, setLandingLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<ResultFilters>(() => readFiltersFromParams(searchParams));

  const [results, setResults] = useState<ProjectResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [barInitialValue, setBarInitialValue] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setLandingLoading(true);
    Promise.all([
      getProjectsLanding(),
      ...FEATURED_OUTCOME_IDS.map((id) =>
        getProjectDetails(id).catch(() => null),
      ),
    ])
      .then(([landing, ...detailResponses]) => {
        if (cancelled) return;
        setStats(landing.stats ?? null);
        const featuredOut: FeaturedOutcome[] = [];
        detailResponses.forEach((res) => {
          if (!res || !res.result) return;
          featuredOut.push({
            id: res.result.id,
            name: res.result.name,
            year: res.result.year,
            type: res.result.type,
            facultyAbbr: pickFacultyAbbr(res.result),
          });
        });
        setFeatured(featuredOut);
      })
      .catch(() => {
        if (cancelled) return;
        setStats(null);
        setFeatured([]);
      })
      .finally(() => {
        if (!cancelled) setLandingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runSearch = async (keywords: string[], ops: Op[], f: ResultFilters) => {
    setSearchLoading(true);
    setHasSearched(true);
    setSearchError(null);
    try {
      const data: ProjectSearchResponse = await searchResults(
        keywords,
        ops,
        f.faculties,
        f.yearFrom,
        f.yearTo,
        f.types,
      );
      setResults(data.results || []);
    } catch (err: any) {
      console.error('Search error:', err);
      setResults([]);
      setSearchError(err?.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const initialQueryRef = useRef<string | null>(searchParams.get('q'));
  useEffect(() => {
    const initialQ = initialQueryRef.current;
    if (!initialQ) return;
    const parsed = readQueryFromParams(new URLSearchParams(window.location.search));
    if (!parsed) return;
    const f = readFiltersFromParams(new URLSearchParams(window.location.search));
    runSearch(parsed.keywords, parsed.ops, f);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (keywords: string[], ops: Op[]) => {
    const next = new URLSearchParams();
    next.set('q', keywords.join(','));
    if (ops.length > 0 && ops.some((o) => o === 'OR')) {
      next.set('ops', ops.join(','));
    }
    if (filters.yearFrom !== undefined) next.set('year_from', String(filters.yearFrom));
    if (filters.yearTo !== undefined) next.set('year_to', String(filters.yearTo));
    if (filters.faculties.length > 0) next.set('faculties', filters.faculties.join(','));
    if (filters.types.length > 0) next.set('types', filters.types.join(','));
    setSearchParams(next);
    runSearch(keywords, ops, filters);
  };

  const runExample = (example: CompetencyExample) => {
    const ops = example.keywords.slice(1).map(() => example.op);
    setBarInitialValue(exampleToQuery(example));
    handleSearch(example.keywords, ops);
  };

  const coverageText = stats
    ? t('landing.coverage', {
        outcomes: stats.outcomes,
        faculties: stats.faculties,
      })
    : null;

  const showFeatured = !hasSearched;

  const skeletonRows = useMemo(() => Array.from({ length: 6 }), []);

  return (
    <PageContainer>
      <PageContent>
        <div className="flex flex-col items-center pt-12 pb-24">
          <h1 className="text-3xl font-semibold tracking-tight text-ctu-text mb-3 text-center">
            {t('pageTitle')}
          </h1>

          <div className="text-sm text-ctu-text-muted text-center max-w-3xl mb-10 min-h-5">
            {landingLoading ? <Skeleton className="h-4 w-72 mx-auto" /> : coverageText}
          </div>

          {showFeatured && (
            <div className="w-full max-w-3xl mb-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted mb-2">
                {t('landing.featuredHeading')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {landingLoading
                  ? Array.from({ length: FEATURED_OUTCOME_IDS.length }).map((_, i) => (
                      <Skeleton key={i} className="col-span-2 h-12 w-full rounded-md" />
                    ))
                  : featured.map((outcome) => {
                      const abbr = outcome.facultyAbbr ?? '';
                      const facultyColor = FACULTY_COLORS[abbr] || DEFAULT_NODE_COLOR;
                      return (
                        <Card
                          key={outcome.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => navigate(`/outcome/${outcome.id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/outcome/${outcome.id}`);
                            }
                          }}
                          className="col-span-2 cursor-pointer border-ctu-border/70 shadow-none transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <CardContent className="flex flex-wrap items-center gap-2 px-3 py-2">
                            {abbr && (
                              <span
                                className="inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-white"
                                style={{ backgroundColor: facultyColor }}
                              >
                                {abbr}
                              </span>
                            )}
                            <span className="flex-1 min-w-0 text-xs font-medium text-ctu-text leading-snug break-words">
                              {outcome.name}
                            </span>
                            <Badge
                              variant="outline"
                              className="h-5 shrink-0 px-1.5 text-xs font-medium tabular-nums bg-white"
                            >
                              {outcome.year}
                            </Badge>
                            <Badge
                              variant="outline"
                              className="h-5 shrink-0 px-1.5 text-xs font-medium bg-white"
                            >
                              {outcome.type}
                            </Badge>
                          </CardContent>
                        </Card>
                      );
                    })}

                {!landingLoading &&
                  COMPETENCY_EXAMPLES.map((example, idx) => (
                    <Card
                      key={`example-${idx}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => runExample(example)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          runExample(example);
                        }
                      }}
                      className="cursor-pointer border-ctu-border/70 shadow-none transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <CardContent className="flex h-full flex-wrap items-center justify-center gap-1.5 px-3 py-2">
                        <Search className="h-3.5 w-3.5 shrink-0 text-ctu-text-muted" aria-hidden />
                        {example.keywords.map((kw, i) => (
                          <span key={i} className="inline-flex items-center gap-1.5">
                            {i > 0 && (
                              <Badge
                                variant="outline"
                                className="h-5 rounded px-1.5 text-[10px] font-semibold uppercase tracking-wide text-ctu-text-muted"
                              >
                                {example.op}
                              </Badge>
                            )}
                            <Badge variant="info" className="h-5 px-1.5 text-xs font-medium">
                              {kw}
                            </Badge>
                          </span>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
              </div>
            </div>
          )}

          <div className="w-full max-w-3xl">
            <ResultsFilterRow value={filters} onChange={setFilters} />
            <ResultSearchBar filters={filters} onSearch={handleSearch} initialValue={barInitialValue} />

            {searchLoading && (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"><Skeleton className="h-4 w-6" /></TableHead>
                      <TableHead className="w-28"><Skeleton className="h-4 w-20" /></TableHead>
                      <TableHead><Skeleton className="h-4 w-24" /></TableHead>
                      <TableHead className="w-20"><Skeleton className="h-4 w-12" /></TableHead>
                      <TableHead className="w-40"><Skeleton className="h-4 w-20" /></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {skeletonRows.map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-6" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-72" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {!searchLoading && searchError && (
              <Alert variant="destructive">
                <AlertDescription>{searchError}</AlertDescription>
              </Alert>
            )}

            {!searchLoading && !searchError && hasSearched && results.length === 0 && (
              <Alert>
                <AlertDescription>{t('results.noResults')}</AlertDescription>
              </Alert>
            )}

            {!searchLoading && !searchError && results.length > 0 && (
              <>
                <p className="mt-4 mb-2 text-sm text-muted-foreground">
                  {t('results.found', { count: results.length })}
                </p>
                <ResultsTable results={results} />
              </>
            )}
          </div>
        </div>
      </PageContent>
    </PageContainer>
  );
}
